<template>
  <div class="hms-page">
    <Navigation />

    <div class="container hms-page-body">
      <div class="d-flex align-items-center justify-content-between mb-3 hms-page-header">
        <div class="d-flex align-items-center gap-3">
          <a href="javascript:void(0)" @click="$router.push('/dashboard')" class="hms-back" title="Back">
            <i class="mdi mdi-arrow-left"></i>
          </a>
          <h4 class="hms-page-title m-0"><i class="mdi mdi-shield-sun me-2"></i>{{ $t('page.cssd', 'CSSD — Sterile Services') }}</h4>
        </div>
        <button class="hms-btn hms-btn-blue" @click="loadAll"><i class="mdi mdi-refresh"></i></button>
      </div>

      <!-- STATS -->
      <div class="hms-stat-row">
        <div class="hms-stat"><div class="hms-stat-num hms-stat-info">{{ stats.today_cycles || 0 }}</div><div class="hms-stat-lbl">Cycles today</div></div>
        <div class="hms-stat"><div class="hms-stat-num hms-stat-success">{{ stats.passed || 0 }}</div><div class="hms-stat-lbl">Passed (30d)</div></div>
        <div class="hms-stat"><div class="hms-stat-num hms-stat-danger">{{ stats.failed || 0 }}</div><div class="hms-stat-lbl">Failed</div></div>
        <div class="hms-stat"><div class="hms-stat-num hms-stat-warning">{{ stats.bi_pending || 0 }}</div><div class="hms-stat-lbl">BI pending</div></div>
        <div class="hms-stat"><div class="hms-stat-num">{{ stats.sterile_packs_available || 0 }}</div><div class="hms-stat-lbl">Sterile packs</div></div>
      </div>

      <!-- TABS -->
      <div class="hms-tab-row">
        <button class="hms-tab" :class="{ active: tab==='stock' }" @click="tab='stock'; loadStock()"><i class="mdi mdi-package-variant me-1"></i>Sterile Stock</button>
        <button class="hms-tab" :class="{ active: tab==='cycles' }" @click="tab='cycles'"><i class="mdi mdi-autorenew me-1"></i>Cycles</button>
        <button class="hms-tab" :class="{ active: tab==='movements' }" @click="tab='movements'; loadMovements()"><i class="mdi mdi-swap-horizontal me-1"></i>Movements</button>
        <button class="hms-tab" :class="{ active: tab==='trays' }" @click="tab='trays'"><i class="mdi mdi-tray-full me-1"></i>Tray Catalog</button>
      </div>

      <!-- ============ STERILE STOCK ============ -->
      <div v-if="tab==='stock'">
        <div class="hms-card">
          <div class="hms-card-head">Sterile pack stock by tray type</div>
          <div class="hms-card-body p-0">
            <table class="hms-table">
              <thead><tr><th>Code</th><th>Tray</th><th>Category</th><th>Available</th><th>Expiring &lt;7d</th><th>Issued</th><th>Expired</th></tr></thead>
              <tbody>
                <tr v-for="s in stock" :key="s.tray_id" :class="s.available == 0 ? 'row-warn' : ''">
                  <td><strong>{{ s.tray_code }}</strong></td>
                  <td>{{ s.tray_name }}</td>
                  <td><span class="hms-pill hms-pill-info">{{ s.tray_category }}</span></td>
                  <td><strong :class="s.available > 0 ? 'st-good' : 'st-bad'">{{ s.available }}</strong></td>
                  <td :class="s.expiring_7d > 0 ? 'st-warn' : ''">{{ s.expiring_7d }}</td>
                  <td>{{ s.issued }}</td>
                  <td class="hms-muted-sm">{{ s.expired }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- ============ CYCLES ============ -->
      <div v-if="tab==='cycles'">
        <div class="d-flex justify-content-between mb-2">
          <select v-model="resultFilter" class="hms-input" @change="loadCycles" style="max-width:200px">
            <option value="">All results</option>
            <option value="passed">Passed</option>
            <option value="failed">Failed</option>
            <option value="quarantined">Quarantined</option>
            <option value="recall">Recalled</option>
          </select>
          <button class="hms-btn hms-btn-success" @click="openCycleModal()"><i class="mdi mdi-plus"></i> Record Cycle</button>
        </div>
        <table class="hms-table">
          <thead><tr><th>Cycle #</th><th>Date</th><th>Type</th><th>Temp/Time</th><th>CI</th><th>BI</th><th>Result</th><th>Loads</th><th>Operator</th><th></th></tr></thead>
          <tbody>
            <tr v-for="c in cycles" :key="c.cycle_id" :class="c.result === 'failed' || c.result === 'recall' ? 'row-action' : (c.result === 'quarantined' ? 'row-warn' : '')">
              <td><strong>{{ c.cycle_number }}</strong></td>
              <td>{{ c.cycle_date }} <span class="hms-muted-sm">{{ c.start_time ? c.start_time.substring(0,5) : '' }}</span></td>
              <td>{{ formatCycleType(c.cycle_type) }}</td>
              <td>{{ c.temperature_c || '—' }}°C / {{ c.duration_min || '—' }}min</td>
              <td><span class="hms-pill" :class="indPill(c.chemical_indicator)">{{ c.chemical_indicator }}</span></td>
              <td><span class="hms-pill" :class="indPill(c.biological_indicator)">{{ c.biological_indicator }}</span></td>
              <td><span class="hms-pill" :class="resultPill(c.result)">{{ c.result }}</span></td>
              <td>{{ c.load_count }}</td>
              <td>{{ c.operator_name || '—' }}</td>
              <td><button class="hms-btn hms-btn-blue hms-btn-sm" @click="openCycleDetail(c)"><i class="mdi mdi-folder-open"></i></button></td>
            </tr>
          </tbody>
        </table>

        <!-- Cycle detail inline panel -->
        <div v-if="cycleDetail.cycle" class="hms-card mt-3">
          <div class="hms-card-head d-flex justify-content-between">
            <span>Cycle {{ cycleDetail.cycle.cycle_number }} — {{ cycleDetail.loads.length }} loads</span>
            <button class="hms-btn hms-btn-secondary hms-btn-sm" @click="cycleDetail.cycle = null">Close</button>
          </div>
          <div class="hms-card-body p-0">
            <table class="hms-table">
              <thead><tr><th>Pack label</th><th>Tray</th><th>Expires</th><th>CI visual</th><th>Status</th><th></th></tr></thead>
              <tbody>
                <tr v-for="l in cycleDetail.loads" :key="l.load_id">
                  <td><strong>{{ l.pack_label }}</strong></td>
                  <td>{{ l.tray_name }}</td>
                  <td :class="expiryClass(l.expiry_date)">{{ l.expiry_date }}</td>
                  <td><span class="hms-pill" :class="indPill(l.chemical_indicator_visual)">{{ l.chemical_indicator_visual }}</span></td>
                  <td><span class="hms-pill" :class="loadStatusPill(l.status)">{{ l.status }}</span></td>
                  <td>
                    <button v-if="l.status === 'sterile'" class="hms-btn hms-btn-success hms-btn-sm" @click="openIssueModal(l)"><i class="mdi mdi-truck-delivery"></i> Issue</button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- ============ MOVEMENTS ============ -->
      <div v-if="tab==='movements'">
        <table class="hms-table">
          <thead><tr><th>When</th><th>Pack</th><th>Tray</th><th>Movement</th><th>From</th><th>To</th><th>Received by</th><th>By</th></tr></thead>
          <tbody>
            <tr v-for="m in movements" :key="m.move_id">
              <td>{{ m.created_at }}</td>
              <td><strong>{{ m.pack_label || '—' }}</strong></td>
              <td>{{ m.tray_name || '—' }}</td>
              <td><span class="hms-pill" :class="movementPill(m.movement_type)">{{ m.movement_type.replace(/_/g, ' ') }}</span></td>
              <td>{{ m.from_location || '—' }}</td>
              <td>{{ m.to_location || '—' }}</td>
              <td>{{ m.received_by_name || '—' }}</td>
              <td>{{ m.recorded_by_name || '—' }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- ============ TRAY CATALOG ============ -->
      <div v-if="tab==='trays'">
        <div class="d-flex justify-content-end mb-2">
          <button class="hms-btn hms-btn-success" @click="openTrayModal()"><i class="mdi mdi-plus"></i> Add Tray</button>
        </div>
        <table class="hms-table">
          <thead><tr><th>Code</th><th>Name</th><th>Category</th><th>Instruments</th><th>Wrap</th><th></th></tr></thead>
          <tbody>
            <tr v-for="t in trays" :key="t.tray_id">
              <td><strong>{{ t.tray_code }}</strong></td>
              <td>{{ t.tray_name }}</td>
              <td><span class="hms-pill hms-pill-info">{{ t.tray_category }}</span></td>
              <td>{{ t.expected_instruments || '—' }}</td>
              <td>{{ t.wrap_material }}</td>
              <td><button class="hms-btn-link" @click="openTrayModal(t)"><i class="mdi mdi-pencil"></i></button></td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- ===== CYCLE MODAL ===== -->
    <div v-if="cycleModal" class="hms-modal-bg" @click.self="cycleModal=false">
      <div class="hms-modal" style="width:780px">
        <div class="hms-modal-head"><h5>Record sterilization cycle</h5><button class="hms-icon-btn" @click="cycleModal=false"><i class="mdi mdi-close"></i></button></div>
        <div class="hms-modal-body">
          <div class="row g-2">
            <div class="col-3"><label class="hms-label">Date *</label><input v-model="cycle.cycle_date" type="date" class="hms-input" /></div>
            <div class="col-3"><label class="hms-label">Start</label><input v-model="cycle.start_time" type="time" class="hms-input" /></div>
            <div class="col-3"><label class="hms-label">End</label><input v-model="cycle.end_time" type="time" class="hms-input" /></div>
            <div class="col-3"><label class="hms-label">Autoclave ID</label><input v-model="cycle.autoclave_id" class="hms-input" placeholder="AUTO-1" /></div>
            <div class="col-3"><label class="hms-label">Type</label>
              <select v-model="cycle.cycle_type" class="hms-input">
                <option value="steam_134">Steam 134°C</option>
                <option value="steam_121">Steam 121°C</option>
                <option value="flash">Flash</option>
                <option value="plasma">Plasma</option>
                <option value="EO">Ethylene Oxide</option>
                <option value="dry_heat">Dry heat</option>
              </select>
            </div>
            <div class="col-3"><label class="hms-label">Temp °C</label><input v-model.number="cycle.temperature_c" type="number" step="0.1" class="hms-input" /></div>
            <div class="col-3"><label class="hms-label">Pressure kPa</label><input v-model.number="cycle.pressure_kpa" type="number" step="0.1" class="hms-input" /></div>
            <div class="col-3"><label class="hms-label">Duration (min)</label><input v-model.number="cycle.duration_min" type="number" class="hms-input" /></div>

            <div class="col-12"><div class="hms-section-label">QUALITY CHECKS</div></div>
            <div class="col-3"><label class="hms-label">Chemical indicator</label>
              <select v-model="cycle.chemical_indicator" class="hms-input"><option value="pass">Pass</option><option value="fail">Fail</option><option value="not_done">Not done</option></select>
            </div>
            <div class="col-3"><label class="hms-label">Biological indicator</label>
              <select v-model="cycle.biological_indicator" class="hms-input"><option value="not_done">Not done</option><option value="pending">Pending</option><option value="pass">Pass</option><option value="fail">Fail</option></select>
            </div>
            <div class="col-3"><label class="hms-label">Bowie-Dick</label>
              <select v-model="cycle.bowie_dick_test" class="hms-input"><option value="not_required">Not required</option><option value="not_done">Not done</option><option value="pass">Pass</option><option value="fail">Fail</option></select>
            </div>
            <div class="col-3"><label class="hms-label">Result</label>
              <select v-model="cycle.result" class="hms-input"><option value="passed">Passed</option><option value="failed">Failed</option><option value="quarantined">Quarantined</option><option value="recall">Recall</option></select>
            </div>

            <div class="col-12"><div class="hms-section-label">LOAD (trays in this cycle)</div></div>
            <div v-for="(ln, i) in cycle.loads" :key="i" class="col-12 hms-load-row">
              <div class="row g-2">
                <div class="col-8">
                  <select v-model.number="ln.tray_id" class="hms-input">
                    <option :value="null">— select tray —</option>
                    <option v-for="t in trays" :key="t.tray_id" :value="t.tray_id">{{ t.tray_code }} — {{ t.tray_name }}</option>
                  </select>
                </div>
                <div class="col-3"><input v-model.number="ln.quantity" type="number" min="1" class="hms-input" placeholder="Qty" /></div>
                <div class="col-1"><button class="hms-btn-link text-danger" @click="cycle.loads.splice(i, 1)"><i class="mdi mdi-trash-can"></i></button></div>
              </div>
            </div>
            <div class="col-12"><button class="hms-btn-link" @click="cycle.loads.push({ tray_id: null, quantity: 1 })">+ Add tray</button></div>

            <div class="col-12"><label class="hms-label">Notes</label><textarea v-model="cycle.notes" class="hms-input" rows="2"></textarea></div>
          </div>
        </div>
        <div class="hms-modal-foot">
          <button class="hms-btn hms-btn-secondary" @click="cycleModal=false">Cancel</button>
          <button class="hms-btn hms-btn-success" :disabled="saving" @click="saveCycle">{{ saving?'Saving…':'Save Cycle' }}</button>
        </div>
      </div>
    </div>

    <!-- ===== TRAY MODAL ===== -->
    <div v-if="trayModal" class="hms-modal-bg" @click.self="trayModal=false">
      <div class="hms-modal" style="width:520px">
        <div class="hms-modal-head"><h5>{{ tray.tray_id ? 'Edit Tray' : 'Add Tray' }}</h5><button class="hms-icon-btn" @click="trayModal=false"><i class="mdi mdi-close"></i></button></div>
        <div class="hms-modal-body">
          <div class="row g-2">
            <div class="col-4"><label class="hms-label">Code *</label><input v-model="tray.tray_code" class="hms-input" /></div>
            <div class="col-8"><label class="hms-label">Name *</label><input v-model="tray.tray_name" class="hms-input" /></div>
            <div class="col-6"><label class="hms-label">Category</label>
              <select v-model="tray.tray_category" class="hms-input">
                <option value="surgical">Surgical</option><option value="obstetric">Obstetric</option>
                <option value="dental">Dental</option><option value="minor_procedure">Minor procedure</option>
                <option value="dressing">Dressing</option><option value="suture_removal">Suture removal</option>
                <option value="linen">Linen</option><option value="other">Other</option>
              </select>
            </div>
            <div class="col-3"><label class="hms-label">Instruments</label><input v-model.number="tray.expected_instruments" type="number" class="hms-input" /></div>
            <div class="col-3"><label class="hms-label">Wrap</label>
              <select v-model="tray.wrap_material" class="hms-input"><option value="linen">Linen</option><option value="crepe_paper">Crepe paper</option><option value="rigid_container">Rigid container</option><option value="pouch">Pouch</option></select>
            </div>
            <div class="col-12"><label class="hms-label">Notes</label><textarea v-model="tray.notes" class="hms-input" rows="2"></textarea></div>
          </div>
        </div>
        <div class="hms-modal-foot">
          <button class="hms-btn hms-btn-secondary" @click="trayModal=false">Cancel</button>
          <button class="hms-btn hms-btn-success" :disabled="saving" @click="saveTray">{{ saving?'Saving…':'Save' }}</button>
        </div>
      </div>
    </div>

    <!-- ===== ISSUE MODAL ===== -->
    <div v-if="issueModal" class="hms-modal-bg" @click.self="issueModal=false">
      <div class="hms-modal" style="width:480px">
        <div class="hms-modal-head"><h5>Issue pack {{ issuePack.pack_label }}</h5><button class="hms-icon-btn" @click="issueModal=false"><i class="mdi mdi-close"></i></button></div>
        <div class="hms-modal-body">
          <div class="row g-2">
            <div class="col-12"><label class="hms-label">To location *</label>
              <select v-model="issueForm.to_location" class="hms-input">
                <option value="">— select —</option>
                <option value="Theatre 1">Theatre 1</option><option value="Theatre 2">Theatre 2</option>
                <option value="Maternity">Maternity</option><option value="Casualty">Casualty</option>
                <option value="Outpatient">Outpatient</option><option value="Wards">Wards</option><option value="Other">Other</option>
              </select>
            </div>
            <div class="col-12"><label class="hms-label">Received by (porter / nurse)</label><input v-model="issueForm.received_by_name" class="hms-input" /></div>
          </div>
        </div>
        <div class="hms-modal-foot">
          <button class="hms-btn hms-btn-secondary" @click="issueModal=false">Cancel</button>
          <button class="hms-btn hms-btn-success" :disabled="saving || !issueForm.to_location" @click="saveIssue">{{ saving?'Issuing…':'Issue' }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Navigation from "@/components/hms-auth/Navigation.vue";
import { executeJson, hms } from "@/api";

export default {
  name: "CssdManagement",
  components: { Navigation },
  data() {
    return {
      tab: 'stock', saving: false,
      stats: {},
      trays: [], stock: [], cycles: [], movements: [],
      resultFilter: '',
      cycleDetail: { cycle: null, loads: [] },

      cycleModal: false, cycle: this.blankCycle(),
      trayModal: false, tray: this.blankTray(),
      issueModal: false, issuePack: {}, issueForm: { to_location: '', received_by_name: '' }
    };
  },
  mounted() { this.loadAll(); },
  methods: {
    blankCycle() {
      const today = new Date();
      return {
        cycle_date: today.toISOString().substring(0,10),
        start_time: today.toTimeString().substring(0,5),
        end_time: '', autoclave_id: 'AUTO-1', cycle_type: 'steam_134',
        temperature_c: 134, pressure_kpa: 200, duration_min: 30,
        chemical_indicator: 'pass', biological_indicator: 'not_done', bowie_dick_test: 'not_required',
        result: 'passed', notes: '', loads: [{ tray_id: null, quantity: 1 }]
      };
    },
    blankTray() { return { tray_id: null, tray_code: '', tray_name: '', tray_category: 'surgical', expected_instruments: null, wrap_material: 'linen', notes: '' }; },

    async loadAll() {
      await Promise.all([this.loadCycles(), this.loadTrays(), this.loadStock()]);
    },
    async loadCycles() {
      try {
        const r = await executeJson({ function: 'getCssdCycles', result: this.resultFilter }, hms);
        if (r.data && r.data.success) {
          const d = r.data.data || {};
          this.cycles = d.Cycles || [];
          this.stats = d.Stats || {};
        }
      } catch (e) { console.error(e); }
    },
    async loadTrays() {
      try {
        const r = await executeJson({ function: 'getCssdTrays' }, hms);
        if (r.data && r.data.success) this.trays = (r.data.data && r.data.data.Trays) || [];
      } catch (e) { console.error(e); }
    },
    async loadStock() {
      try {
        const r = await executeJson({ function: 'getSterileStock' }, hms);
        if (r.data && r.data.success) this.stock = (r.data.data && r.data.data.Stock) || [];
      } catch (e) { console.error(e); }
    },
    async loadMovements() {
      try {
        const r = await executeJson({ function: 'getCssdMovements' }, hms);
        if (r.data && r.data.success) this.movements = (r.data.data && r.data.data.Movements) || [];
      } catch (e) { console.error(e); }
    },

    openCycleModal() { this.cycle = this.blankCycle(); this.cycleModal = true; },
    async saveCycle() {
      const loads = (this.cycle.loads || []).filter(l => l.tray_id && l.quantity > 0);
      if (!loads.length) { alert('Add at least one tray.'); return; }
      this.saving = true;
      try {
        const r = await executeJson({ function: 'recordCssdCycle', ...this.cycle, loads }, hms);
        if (r.data && r.data.success) { this.cycleModal = false; await this.loadAll(); }
        else alert((r.data && r.data.message) || 'Failed.');
      } catch (e) { console.error(e); }
      finally { this.saving = false; }
    },

    async openCycleDetail(c) {
      try {
        const r = await executeJson({ function: 'getCssdCycleDetail', cycle_id: c.cycle_id }, hms);
        if (r.data && r.data.success) {
          const d = r.data.data || {};
          this.cycleDetail = { cycle: d.Cycle, loads: d.Loads || [] };
        }
      } catch (e) { console.error(e); }
    },

    openIssueModal(load) { this.issuePack = load; this.issueForm = { to_location: '', received_by_name: '' }; this.issueModal = true; },
    async saveIssue() {
      this.saving = true;
      try {
        const r = await executeJson({ function: 'issueCssdPack', load_id: this.issuePack.load_id, ...this.issueForm }, hms);
        if (r.data && r.data.success) {
          this.issueModal = false;
          if (this.cycleDetail.cycle) await this.openCycleDetail(this.cycleDetail.cycle);
          await this.loadStock();
        } else alert((r.data && r.data.message) || 'Failed.');
      } catch (e) { console.error(e); }
      finally { this.saving = false; }
    },

    openTrayModal(t) { this.tray = t ? { ...this.blankTray(), ...t } : this.blankTray(); this.trayModal = true; },
    async saveTray() {
      this.saving = true;
      try {
        const r = await executeJson({ function: 'saveCssdTray', ...this.tray }, hms);
        if (r.data && r.data.success) { this.trayModal = false; await this.loadTrays(); }
      } catch (e) { console.error(e); }
      finally { this.saving = false; }
    },

    indPill(v) { return ({ pass: 'hms-pill-success', fail: 'hms-pill-danger', pending: 'hms-pill-warn', not_done: 'hms-pill-muted', not_required: 'hms-pill-muted', not_checked: 'hms-pill-muted' })[v] || ''; },
    resultPill(r) { return ({ passed: 'hms-pill-success', failed: 'hms-pill-danger', quarantined: 'hms-pill-warn', recall: 'hms-pill-danger' })[r] || ''; },
    loadStatusPill(s) { return ({ sterile: 'hms-pill-success', issued: 'hms-pill-info', returned_dirty: 'hms-pill-muted', expired: 'hms-pill-warn', recalled: 'hms-pill-danger', wasted: 'hms-pill-muted' })[s] || ''; },
    movementPill(m) { return ({ issued: 'hms-pill-info', returned_dirty: 'hms-pill-warn', received_dirty: 'hms-pill-muted', condemned: 'hms-pill-danger', transferred: 'hms-pill-info', lost: 'hms-pill-danger' })[m] || ''; },
    formatCycleType(t) { return ({ steam_134: 'Steam 134°C', steam_121: 'Steam 121°C', flash: 'Flash', plasma: 'Plasma', EO: 'EO', dry_heat: 'Dry heat' })[t] || t; },
    expiryClass(d) {
      if (!d) return '';
      const days = Math.floor((new Date(d).getTime() - Date.now()) / 86400000);
      if (days < 0) return 'st-bad';
      if (days < 7) return 'st-warn';
      return '';
    }
  }
};
</script>

<style scoped>
.hms-page-body { padding: 16px 0 32px; }
.hms-back { color: #4a5568; font-size: 20px; text-decoration: none; }
.hms-page-title { font-size: 18px; font-weight: 600; color: #2d3748; }

.hms-stat-row { display: grid; grid-template-columns: repeat(5, 1fr); gap: 12px; margin-bottom: 16px; }
.hms-stat { background: #fff; border: 1px solid #e2e8f0; border-radius: 8px; padding: 14px; text-align: center; }
.hms-stat-num { font-size: 26px; font-weight: 700; color: #1a202c; }
.hms-stat-lbl { font-size: 11px; color: #718096; text-transform: uppercase; letter-spacing: .5px; margin-top: 2px; }
.hms-stat-info { color: #2c5282; } .hms-stat-success { color: #276749; } .hms-stat-warning { color: #c05621; } .hms-stat-danger { color: #c53030; }

.hms-tab-row { display: flex; gap: 4px; border-bottom: 1px solid #e2e8f0; margin-bottom: 16px; }
.hms-tab { padding: 10px 16px; border: none; background: none; cursor: pointer; color: #718096; border-bottom: 2px solid transparent; font-size: 14px; }
.hms-tab.active { color: #2b6cb0; border-bottom-color: #2b6cb0; font-weight: 600; }

.hms-card { background: #fff; border: 1px solid #e2e8f0; border-radius: 8px; }
.hms-card-head { padding: 10px 14px; border-bottom: 1px solid #e2e8f0; font-weight: 600; color: #2d3748; }
.hms-card-body { padding: 14px; }
.hms-card-body.p-0 { padding: 0; }

.hms-section-label { font-size: 11px; font-weight: 700; color: #4a5568; letter-spacing: 1px; padding: 4px 0; border-bottom: 1px solid #edf2f7; margin: 8px 0 6px; }

.hms-table { width: 100%; border-collapse: collapse; font-size: 13px; background: #fff; }
.hms-table th { background: #f7fafc; padding: 9px; border-bottom: 1px solid #e2e8f0; font-weight: 600; color: #4a5568; text-align: left; }
.hms-table td { padding: 8px 9px; border-bottom: 1px solid #edf2f7; vertical-align: middle; }
.hms-table tr.row-warn   { background: #fffaf0; }
.hms-table tr.row-action { background: #fff5f5; }

.hms-input { width: 100%; padding: 6px 10px; border: 1px solid #cbd5e0; border-radius: 6px; font-size: 13px; }
.hms-label { font-size: 11px; font-weight: 600; color: #4a5568; display: block; margin-bottom: 3px; }
.hms-load-row { background: #f7fafc; padding: 6px; border-radius: 4px; margin-bottom: 4px; }

.hms-pill { display: inline-block; padding: 2px 8px; border-radius: 10px; font-size: 11px; font-weight: 600; }
.hms-pill-success { background: #c6f6d5; color: #22543d; }
.hms-pill-info    { background: #bee3f8; color: #2a4365; }
.hms-pill-warn    { background: #feebc8; color: #7b341e; }
.hms-pill-danger  { background: #fed7d7; color: #742a2a; }
.hms-pill-muted   { background: #edf2f7; color: #4a5568; }

.hms-modal-bg { position: fixed; inset: 0; background: rgba(0,0,0,.5); display: flex; align-items: center; justify-content: center; z-index: 100; padding: 20px; }
.hms-modal { background: #fff; border-radius: 8px; max-height: 92vh; display: flex; flex-direction: column; }
.hms-modal-head { padding: 14px 18px; border-bottom: 1px solid #e2e8f0; display: flex; justify-content: space-between; align-items: center; }
.hms-modal-body { padding: 16px 18px; overflow-y: auto; }
.hms-modal-foot { padding: 12px 18px; border-top: 1px solid #e2e8f0; display: flex; justify-content: flex-end; gap: 8px; }
.hms-icon-btn { background: none; border: none; cursor: pointer; }

.hms-btn { display: inline-flex; align-items: center; gap: 4px; padding: 7px 12px; border-radius: 6px; border: 1px solid transparent; font-size: 13px; font-weight: 500; cursor: pointer; }
.hms-btn-sm { padding: 4px 10px; font-size: 12px; }
.hms-btn-blue      { background: #2b6cb0; color: #fff; }
.hms-btn-success   { background: #38a169; color: #fff; }
.hms-btn-secondary { background: #fff; color: #2d3748; border: 1px solid #cbd5e0; }
.hms-btn-link      { background: transparent; color: #2b6cb0; border: none; cursor: pointer; }
.text-danger { color: #c53030; }

.st-good { color: #276749; font-weight: 700; }
.st-bad  { color: #c53030; font-weight: 700; }
.st-warn { color: #b45309; font-weight: 600; }
.hms-muted-sm { color: #a0aec0; font-size: 11px; }
</style>
